import { useState, useEffect, useCallback, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { Button, Text, Colors, Icon } from '@mercator-gcp/grout';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import Header from 'components/widgets/SearchFilterWizard/steps/components/Header';
import { useGroupConfigurations } from 'hooks/useGroupConfig';
import { useSaveGroup } from 'hooks/useSaveGroup';
import ElasticSearchFilterAPI from 'services/API/ElasticSearchFilterAPI';
import { useUser } from 'state/auth';
import {
  useClearGroup,
  useDashboardVerticals,
  useDashboardVerticalsExclusions,
  useDashboardCostIncludeUnknownState,
  useWorkTypesExclusions,
  useDashboardTriggers,
  useDashboardCompanyStakeholderTypesExclusions,
} from 'state/dashboard';
import { useSectors } from 'state/projectTypes';
import { trackPageView } from 'utils/analytics';
import { GROUP_TYPE, getDefaultEndDate } from 'utils/constants';
import CompletionStep from './steps/CompletionStep'; // eslint-disable-line no-restricted-imports
import ExcludeSectorsStep from './steps/ExcludeSectorsStep'; // eslint-disable-line no-restricted-imports
import GeographicFocusStep from './steps/GeographicFocusStep'; // eslint-disable-line no-restricted-imports
import LatestProjectStageStep from './steps/LatestProjectStageStep'; // eslint-disable-line no-restricted-imports
import ProjectValueStep from './steps/ProjectValueStep'; // eslint-disable-line no-restricted-imports
import RegionSelectionStep from './steps/RegionSelectionStep'; // eslint-disable-line no-restricted-imports
import SavingStep from './steps/SavingStep'; // eslint-disable-line no-restricted-imports
import SearchNameStep from './steps/SearchNameStep'; // eslint-disable-line no-restricted-imports
import SectorsStep from './steps/SectorsStep'; // eslint-disable-line no-restricted-imports
import WorkTypeStep from './steps/WorkTypeStep'; // eslint-disable-line no-restricted-imports

enum WizardStep {
  GEOGRAPHIC_FOCUS = 0,
  REGION_SELECTION = 1,
  LATEST_PROJECT_STAGE = 2,
  SECTORS = 3,
  EXCLUDE_SECTORS = 4,
  WORK_TYPE = 5,
  PROJECT_VALUE = 6,
  SEARCH_NAME = 7,
  SAVING = 8,
  COMPLETION = 9,
}

// Map wizard steps to analytics sub-section names
const getSubSectionName = (
  step: WizardStep,
  isFirstSearch: boolean,
): string | undefined => {
  const prefix = isFirstSearch ? 'first_search' : 'new_search';

  const stepToSubSectionMap: Record<WizardStep, string | undefined> = {
    [WizardStep.GEOGRAPHIC_FOCUS]: `${prefix}_location`,
    [WizardStep.REGION_SELECTION]: `${prefix}_location`, // Same as GEOGRAPHIC_FOCUS since combined
    [WizardStep.LATEST_PROJECT_STAGE]: `${prefix}_stages`,
    [WizardStep.SECTORS]: `${prefix}_sectors`,
    [WizardStep.EXCLUDE_SECTORS]: `${prefix}_exclude_sectors`,
    [WizardStep.WORK_TYPE]: `${prefix}_worktype`,
    [WizardStep.PROJECT_VALUE]: `${prefix}_projectvalue`,
    [WizardStep.SEARCH_NAME]: `${prefix}_searchname`,
    [WizardStep.SAVING]: undefined, // Don't track loading/completion steps
    [WizardStep.COMPLETION]: undefined,
  };

  return stepToSubSectionMap[step];
};

const wizardContainerStyle = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: 'padding_32',
  paddingTop: '40px', // Minimum 40px from top bar to prevent content jumping
  paddingBottom: 'padding_32', // Keep bottom padding for natural flow
  maxWidth: '800px',
  margin: '0 auto',
  '@media (max-width: 1280px)': {
    padding: 'padding_24',
    paddingTop: '24px', // Reduced top padding on smaller screens
  },
  '@media (max-width: 1024px)': {
    padding: 'padding_16',
    paddingTop: '16px', // Further reduced top padding on tablets
  },
};

const welcomeSectionStyle = {
  marginBottom: 'padding_32',
  '@media (max-width: 1280px)': {
    marginBottom: 'padding_24',
  },
  '@media (max-width: 1024px)': {
    marginBottom: 'padding_16',
  },
};

const contentSectionStyle = {
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: 'padding_32', // Add bottom padding to content to prevent it from showing behind sticky footer
  '@media (max-width: 1280px)': {
    paddingBottom: 'padding_24',
  },
  '@media (max-width: 1024px)': {
    paddingBottom: 'padding_16',
  },
};

const footerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 'padding_32',
  paddingTop: 'padding_24',
  paddingBottom: 'padding_32',
  borderTop: `1px solid ${Colors.ArmyGreen200}`,
  backgroundColor: Colors.White, // Ensure footer covers content when sticky
  position: 'sticky',
  bottom: '-16px', // Account for Layout's paddingBottom: 'padding_16' to eliminate gap
  marginBottom: '-16px', // Negative margin to compensate
  zIndex: 10, // Ensure footer stays above content when scrolling
  '@media (max-width: 1280px)': {
    marginTop: 'padding_24',
    paddingTop: 'padding_16',
    paddingBottom: 'padding_24',
  },
  '@media (max-width: 1024px)': {
    marginTop: 'padding_16',
    paddingTop: 'padding_12',
    paddingBottom: 'padding_16',
    flexWrap: 'wrap',
    gap: 'padding_8',
  },
};

const stepIndicatorTextStyle = {
  padding: 'padding_8',
  paddingTop: 'padding_24',
  whiteSpace: 'nowrap' as const,
};

type SearchFilterWizardProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (groupId: number) => void;
  isFirstSearch?: boolean; // If true, shows "first search" copy; if false, shows "new search" copy
};

const SearchFilterWizard = ({
  isOpen,
  onClose,
  onSuccess,
  isFirstSearch = true,
}: SearchFilterWizardProps) => {
  const { user } = useUser();
  useGroupConfigurations({
    userId: String(user?.id),
    groupType: GROUP_TYPE.PROJECT,
  });
  const { clearGroup } = useClearGroup();
  const { saveGroup } = useSaveGroup();
  const navigate = useNavigate();
  const { verticals: allVerticals } = useSectors();
  const [, setVerticals] = useDashboardVerticals();
  const [, setVerticalExclusions] = useDashboardVerticalsExclusions();
  const [includeUnknownCost] = useDashboardCostIncludeUnknownState();
  const [workTypesExclusions] = useWorkTypesExclusions();
  const [triggers] = useDashboardTriggers();
  const [stakeholderExclusions, setStakeholderExclusions] =
    useDashboardCompanyStakeholderTypesExclusions();

  const resetGlobalFiltersForWizard = useCallback(() => {
    clearGroup();
    setVerticals([]);
    setVerticalExclusions([]);
    setStakeholderExclusions('');
  }, [clearGroup, setVerticals, setVerticalExclusions, setStakeholderExclusions]);

  const [currentStep, setCurrentStep] = useState<WizardStep>(WizardStep.SEARCH_NAME);
  const [searchName, setSearchName] = useState('');
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedVerticals, setSelectedVerticals] = useState<number[]>([]);
  const [excludedVerticals, setExcludedVerticals] = useState<number[]>([]);
  const [selectedWorkTypes, setSelectedWorkTypes] = useState<string[]>([]);
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [projectValue, setProjectValue] = useState<{ min: number; max: number }>({
    min: 0,
    max: 0,
  });

  const skipsProjectValueStep = useMemo(
    () => selectedStages.includes('general_contractor'),
    [selectedStages],
  );

  // Clear filters when wizard opens
  useEffect(() => {
    if (isOpen) {
      resetGlobalFiltersForWizard();
      setCurrentStep(WizardStep.GEOGRAPHIC_FOCUS);
      setSearchName('');
      setSelectedState(null);
      setSelectedRegions([]);
      setSelectedVerticals([]);
      setExcludedVerticals([]);
      setSelectedWorkTypes([]);
      setSelectedStages([]);
      setProjectValue({ min: 0, max: 0 });
    }
  }, [isOpen, resetGlobalFiltersForWizard]);

  // Sync selected verticals to global state whenever they change
  useEffect(() => {
    setVerticals(selectedVerticals);
  }, [selectedVerticals, setVerticals]);

  // Sync excluded verticals to global state whenever they change
  useEffect(() => {
    setVerticalExclusions(excludedVerticals);
  }, [excludedVerticals, setVerticalExclusions]);

  // Track analytics page view when step changes
  useEffect(() => {
    if (isOpen) {
      const subSectionName = getSubSectionName(currentStep, isFirstSearch);
      if (subSectionName) {
        trackPageView({
          sectionName: 'project_groups',
          subSectionName,
        });
      }
    }
  }, [currentStep, isOpen, isFirstSearch]);

  // Build filter request for preview (only when we have required filters)
  const filterRequestForPreview = useMemo(() => {
    // Only build request if we have regions
    if (selectedRegions.length === 0) {
      return null;
    }

    const defaultEndDate = getDefaultEndDate();
    const metropolitanIds = { mercator_metropolitan_ids: selectedRegions };

    // Transform triggers from dashboard state format (camelCase) to API format (snake_case)
    const transformedTriggers =
      triggers.length > 0
        ? triggers.map(({ cityId, documentTypes }) => ({
            city_id: cityId,
            document_types: documentTypes
              .map(({ categories, statuses, id: document_type_id }) => ({
                document_type_id,
                categories,
                document_statuses: statuses,
              }))
              .filter(
                ({ document_statuses, categories }) =>
                  document_statuses.length !== 0 || categories.length !== 0,
              ),
          }))
        : undefined;

    return {
      ...metropolitanIds,
      page: 1,
      ...(selectedVerticals.length > 0 && { verticals: selectedVerticals }),
      ...(excludedVerticals.length > 0 && { verticals_exclusions: excludedVerticals }),
      ...(projectValue.min > 0 && { min_project_cost: projectValue.min }),
      ...(projectValue.max > 0 && { max_project_cost: projectValue.max }),
      include_unknown_cost: includeUnknownCost,
      ...(selectedWorkTypes.length > 0 && { work_type_ids: selectedWorkTypes }),
      ...(workTypesExclusions.length > 0 && {
        work_type_ids_exclusions: workTypesExclusions,
      }),
      ...(transformedTriggers &&
        transformedTriggers.length > 0 && { triggers: transformedTriggers }),
      ...(stakeholderExclusions && {
        company_types_exclusions: stakeholderExclusions.split(',').filter(Boolean),
      }),
      latest_activity_end: defaultEndDate,
    };
  }, [
    selectedRegions,
    selectedVerticals,
    excludedVerticals,
    projectValue,
    includeUnknownCost,
    selectedWorkTypes,
    workTypesExclusions,
    triggers,
    stakeholderExclusions,
  ]);

  // Preview result count - only fetch from SECTORS step onwards
  const { data: previewData, isLoading: isPreviewLoading } = useQuery(
    ['wizard-preview-count', filterRequestForPreview, currentStep],
    async () => {
      if (!filterRequestForPreview) {
        return { count: 0 };
      }
      const response = await ElasticSearchFilterAPI.postFilterProjects({
        ...filterRequestForPreview,
        page: 1,
      });

      return { count: response.data.count || 0 };
    },
    {
      enabled:
        !!filterRequestForPreview &&
        selectedRegions.length > 0 &&
        currentStep >= WizardStep.SECTORS,
      refetchOnWindowFocus: false,
      staleTime: 5000, // Cache for 5 seconds
    },
  );

  const resultCount = previewData?.count ?? null;
  const hasZeroResults = resultCount !== null && resultCount === 0;

  const handleNext = useCallback(() => {
    if (currentStep < WizardStep.COMPLETION) {
      let nextStep = currentStep + 1;
      // Skip REGION_SELECTION step since it's combined with GEOGRAPHIC_FOCUS
      if (nextStep === WizardStep.REGION_SELECTION) {
        nextStep = WizardStep.LATEST_PROJECT_STAGE;
      }
      if (
        currentStep === WizardStep.WORK_TYPE &&
        selectedStages.includes('general_contractor')
      ) {
        nextStep = WizardStep.SEARCH_NAME;
      }
      setCurrentStep(nextStep as WizardStep);
    }
  }, [currentStep, selectedStages]);

  const handleSkip = useCallback(() => {
    // Don't allow skipping the final step (SEARCH_NAME) as it's required
    if (currentStep === WizardStep.SEARCH_NAME) {
      return;
    }

    // Don't allow skipping Geographic Focus (location step)
    if (currentStep === WizardStep.GEOGRAPHIC_FOCUS) {
      return;
    }

    // Don't allow skipping Preferred Project Stages (LATEST_PROJECT_STAGE)
    if (currentStep === WizardStep.LATEST_PROJECT_STAGE) {
      return;
    }

    if (currentStep < WizardStep.COMPLETION) {
      let nextStep = currentStep + 1;
      // Skip REGION_SELECTION step since it's combined with GEOGRAPHIC_FOCUS
      if (nextStep === WizardStep.REGION_SELECTION) {
        nextStep = WizardStep.LATEST_PROJECT_STAGE;
      }
      if (
        currentStep === WizardStep.WORK_TYPE &&
        selectedStages.includes('general_contractor')
      ) {
        nextStep = WizardStep.SEARCH_NAME;
      }
      setCurrentStep(nextStep as WizardStep);
    }
  }, [currentStep, selectedStages]);

  const handleBack = useCallback(() => {
    if (currentStep > WizardStep.GEOGRAPHIC_FOCUS) {
      let prevStep = currentStep - 1;
      // Skip REGION_SELECTION step when going back
      if (prevStep === WizardStep.REGION_SELECTION) {
        prevStep = WizardStep.GEOGRAPHIC_FOCUS;
      }
      if (
        currentStep === WizardStep.SEARCH_NAME &&
        selectedStages.includes('general_contractor')
      ) {
        prevStep = WizardStep.WORK_TYPE;
      }
      setCurrentStep(prevStep as WizardStep);
    }
  }, [currentStep, selectedStages]);

  const handleCancel = useCallback(() => {
    if (saveGroup.isLoading) {
      return;
    }
    resetGlobalFiltersForWizard();
    onClose();
  }, [saveGroup.isLoading, resetGlobalFiltersForWizard, onClose]);

  const handleSave = useCallback(() => {
    // Sync both selected and excluded verticals to global state before saving
    setVerticals(selectedVerticals);
    setVerticalExclusions(excludedVerticals);
    setCurrentStep(WizardStep.SAVING);
    saveGroup.mutate({
      onSuccessCallback: (groupId: number) => {
        setCurrentStep(WizardStep.COMPLETION);
        // Call onSuccess callback if provided, otherwise navigate
        if (onSuccess) {
          setTimeout(() => {
            onSuccess(groupId);
            // Don't close wizard here - let the parent handle closing after refetch
          }, 2000);
        } else {
          // Navigate to dashboard with saved search after a short delay
          setTimeout(() => {
            navigate(`/dashboard/${groupId}`);
            onClose();
          }, 2000);
        }
      },
      onErrorCallback: () => {
        setCurrentStep(
          selectedStages.includes('general_contractor')
            ? WizardStep.SEARCH_NAME
            : WizardStep.PROJECT_VALUE,
        );
      },
    });
  }, [
    saveGroup,
    navigate,
    onClose,
    onSuccess,
    selectedStages,
    selectedVerticals,
    excludedVerticals,
    setVerticals,
    setVerticalExclusions,
  ]);

  const canProceed = useCallback(() => {
    // Disable Next if filters yield 0 results (except on first step and search name step)
    if (
      currentStep !== WizardStep.GEOGRAPHIC_FOCUS &&
      currentStep !== WizardStep.SEARCH_NAME &&
      hasZeroResults
    ) {
      return false;
    }

    switch (currentStep) {
      case WizardStep.GEOGRAPHIC_FOCUS:
        return selectedState !== null && selectedRegions.length > 0;
      case WizardStep.REGION_SELECTION:
        return selectedRegions.length > 0;
      case WizardStep.LATEST_PROJECT_STAGE:
        return selectedStages.length > 0; // Now represents selected role
      case WizardStep.SECTORS:
        return selectedVerticals.length > 0;
      case WizardStep.EXCLUDE_SECTORS:
        return true; // Optional step
      case WizardStep.WORK_TYPE:
        return true; // Optional step
      case WizardStep.PROJECT_VALUE:
        return true; // Optional step
      case WizardStep.SEARCH_NAME:
        return searchName.trim().length > 0;
      default:
        return true;
    }
  }, [
    currentStep,
    searchName,
    hasZeroResults,
    selectedState,
    selectedRegions,
    selectedVerticals,
    selectedStages,
  ]);

  const renderStep = () => {
    switch (currentStep) {
      case WizardStep.GEOGRAPHIC_FOCUS:
        return (
          <GeographicFocusStep
            selectedState={selectedState}
            onStateChange={setSelectedState}
            selectedRegions={selectedRegions}
            onRegionsChange={setSelectedRegions}
          />
        );
      case WizardStep.REGION_SELECTION:
        return (
          <RegionSelectionStep
            selectedState={selectedState}
            selectedRegions={selectedRegions}
            onRegionsChange={setSelectedRegions}
          />
        );
      case WizardStep.LATEST_PROJECT_STAGE:
        return (
          <LatestProjectStageStep
            selectedStages={selectedStages}
            onStagesChange={setSelectedStages}
            selectedState={selectedState}
          />
        );
      case WizardStep.SECTORS:
        return (
          <SectorsStep
            selectedVerticals={selectedVerticals}
            onVerticalsChange={setSelectedVerticals}
          />
        );
      case WizardStep.EXCLUDE_SECTORS:
        return (
          <ExcludeSectorsStep
            excludedVerticals={excludedVerticals}
            onExcludedVerticalsChange={setExcludedVerticals}
            selectedVerticals={selectedVerticals}
          />
        );
      case WizardStep.WORK_TYPE:
        return (
          <WorkTypeStep
            selectedWorkTypes={selectedWorkTypes}
            onWorkTypesChange={setSelectedWorkTypes}
          />
        );
      case WizardStep.PROJECT_VALUE:
        return (
          <ProjectValueStep
            projectValue={projectValue}
            onProjectValueChange={setProjectValue}
          />
        );
      case WizardStep.SEARCH_NAME:
        return (
          <SearchNameStep
            searchName={searchName}
            onSearchNameChange={setSearchName}
            selectedState={selectedState}
            selectedVerticals={selectedVerticals}
            allVerticals={allVerticals}
          />
        );
      case WizardStep.SAVING:
        return <SavingStep />;
      case WizardStep.COMPLETION:
        return <CompletionStep />;
      default:
        return null;
    }
  };

  // Map wizard steps to display step numbers (skipping REGION_SELECTION since it's combined with GEOGRAPHIC_FOCUS)
  const getDisplayStepNumber = useCallback(
    (step: WizardStep) => {
      if (skipsProjectValueStep) {
        const stepMapGc: Record<WizardStep, number> = {
          [WizardStep.GEOGRAPHIC_FOCUS]: 1,
          [WizardStep.REGION_SELECTION]: 1,
          [WizardStep.LATEST_PROJECT_STAGE]: 2,
          [WizardStep.SECTORS]: 3,
          [WizardStep.EXCLUDE_SECTORS]: 4,
          [WizardStep.WORK_TYPE]: 5,
          [WizardStep.PROJECT_VALUE]: 6,
          [WizardStep.SEARCH_NAME]: 6,
          [WizardStep.SAVING]: 6,
          [WizardStep.COMPLETION]: 6,
        };

        return stepMapGc[step] || 1;
      }
      const stepMap: Record<WizardStep, number> = {
        [WizardStep.GEOGRAPHIC_FOCUS]: 1,
        [WizardStep.REGION_SELECTION]: 1, // Same as GEOGRAPHIC_FOCUS since combined
        [WizardStep.LATEST_PROJECT_STAGE]: 2,
        [WizardStep.SECTORS]: 3,
        [WizardStep.EXCLUDE_SECTORS]: 4,
        [WizardStep.WORK_TYPE]: 5,
        [WizardStep.PROJECT_VALUE]: 6,
        [WizardStep.SEARCH_NAME]: 7,
        [WizardStep.SAVING]: 8,
        [WizardStep.COMPLETION]: 8,
      };

      return stepMap[step] || 1;
    },
    [skipsProjectValueStep],
  );

  const totalSteps = skipsProjectValueStep ? 6 : 7; // GC skips project value: 6 steps; otherwise 7
  const currentStepNumber =
    currentStep < WizardStep.SAVING ? getDisplayStepNumber(currentStep) : totalSteps;

  if (!isOpen) {
    return null;
  }

  return (
    <Box sx={wizardContainerStyle}>
      {/* Welcome Section */}
      {currentStep === WizardStep.GEOGRAPHIC_FOCUS && (
        <Box sx={welcomeSectionStyle}>
          <FlexColumn sx={{ gap: 0 }}>
            {isFirstSearch ? (
              <>
                <Text.Large sx={{ marginBottom: 'padding_8' }}>
                  Hi {user?.first_name || 'there'}, welcome to Mercator.ai. 👋
                </Text.Large>
                <Text.Large sx={{ marginBottom: 'padding_24' }}>
                  Let&apos;s set up your first saved search so you can start discovering
                  projects right away.
                </Text.Large>
              </>
            ) : (
              <>
                <Text.Large sx={{ marginBottom: 'padding_8' }}>
                  Create a new saved search
                </Text.Large>
                <Text.Large sx={{ marginBottom: 'padding_24' }}>
                  Let&apos;s set up another saved search to help you discover more
                  projects.
                </Text.Large>
              </>
            )}
            <Header
              title="Where are you looking for your next projects?"
              description=""
            />
          </FlexColumn>
        </Box>
      )}

      {/* Step Content */}
      <Box sx={contentSectionStyle}>{renderStep()}</Box>

      {/* Footer with Step Indicator and Navigation */}
      {currentStep < WizardStep.SAVING && (
        <>
          {/* On GEOGRAPHIC_FOCUS step: Cancel always; Next when regions are selected */}
          {currentStep === WizardStep.GEOGRAPHIC_FOCUS ? (
            <FlexRow sx={footerStyle}>
              <FlexRow sx={{ gap: 'padding_12', alignItems: 'center', flex: 1 }}>
                <Button
                  variant="functional"
                  onClick={handleCancel}
                  isDisabled={saveGroup.isLoading}
                >
                  Cancel
                </Button>
              </FlexRow>
              <FlexRow sx={{ alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                <Text.SmallLabel
                  as="div"
                  color={Colors.TextQuiet}
                  sx={stepIndicatorTextStyle}
                >
                  STEP {currentStepNumber}/{totalSteps}
                </Text.SmallLabel>
              </FlexRow>
              <FlexRow sx={{ gap: 'padding_12', justifyContent: 'flex-end', flex: 1 }}>
                <Button
                  variant="primary"
                  onClick={handleNext}
                  isDisabled={!canProceed()}
                  endAdornment={
                    <Icon
                      name={Icon.Name.ChevronRight}
                      size={16}
                      fill={!canProceed() ? Colors.TextQuiet : Colors.White}
                    />
                  }
                >
                  Next
                </Button>
              </FlexRow>
            </FlexRow>
          ) : (
            <FlexRow sx={footerStyle}>
              <FlexRow sx={{ gap: 'padding_12', alignItems: 'center', flex: 1 }}>
                <Button
                  variant="functional"
                  onClick={handleCancel}
                  isDisabled={saveGroup.isLoading}
                >
                  Cancel
                </Button>
              </FlexRow>
              <FlexRow sx={{ alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                <Text.SmallLabel
                  as="div"
                  color={Colors.TextQuiet}
                  sx={stepIndicatorTextStyle}
                >
                  STEP {currentStepNumber}/{totalSteps}
                  {currentStep >= WizardStep.SECTORS &&
                    currentStep !== WizardStep.SEARCH_NAME && (
                      <>
                        {'  |  '}
                        {hasZeroResults ? (
                          'No projects found. Try adjusting your filters.'
                        ) : (
                          <>
                            {isPreviewLoading ? (
                              <Box
                                as="span"
                                sx={{
                                  display: 'inline-block',
                                  width: '40px',
                                  height: '0.5em',
                                  backgroundColor: Colors.TextQuiet,
                                  opacity: 0.15,
                                  borderRadius: '2px',
                                  verticalAlign: 'middle',
                                  marginRight: '4px',
                                }}
                              />
                            ) : (
                              resultCount?.toLocaleString() || '0'
                            )}{' '}
                            projects found
                          </>
                        )}
                      </>
                    )}
                </Text.SmallLabel>
              </FlexRow>
              <FlexRow sx={{ gap: 'padding_12', justifyContent: 'flex-end', flex: 1 }}>
                {currentStep > WizardStep.GEOGRAPHIC_FOCUS && (
                  <Button
                    variant="functional"
                    onClick={handleBack}
                    startAdornment={
                      <Icon
                        name={Icon.Name.ChevronLeft}
                        size={16}
                        fill={Colors.TextQuiet}
                      />
                    }
                  >
                    Back
                  </Button>
                )}
                {/* Show Skip if step is skippable and no selections made, otherwise show Next */}
                {(() => {
                  const isSkippableStep =
                    currentStep !== WizardStep.SEARCH_NAME &&
                    currentStep !== WizardStep.LATEST_PROJECT_STAGE;

                  const hasSelections = (() => {
                    switch (currentStep) {
                      case WizardStep.SECTORS:
                        return selectedVerticals.length > 0;
                      case WizardStep.EXCLUDE_SECTORS:
                        return excludedVerticals.length > 0;
                      case WizardStep.WORK_TYPE:
                        return selectedWorkTypes.length > 0;
                      case WizardStep.PROJECT_VALUE:
                        return projectValue.min > 0 || projectValue.max > 0;
                      default:
                        return canProceed();
                    }
                  })();

                  return isSkippableStep && !hasSelections ? (
                    <Button variant="primary" onClick={handleSkip}>
                      Skip
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={
                        currentStep === WizardStep.SEARCH_NAME ? handleSave : handleNext
                      }
                      isDisabled={!canProceed()}
                      endAdornment={
                        currentStep !== WizardStep.SEARCH_NAME ? (
                          <Icon
                            name={Icon.Name.ChevronRight}
                            size={16}
                            fill={!canProceed() ? Colors.TextQuiet : Colors.White}
                          />
                        ) : undefined
                      }
                    >
                      {currentStep === WizardStep.SEARCH_NAME
                        ? 'Save & Continue'
                        : 'Next'}
                    </Button>
                  );
                })()}
              </FlexRow>
            </FlexRow>
          )}
        </>
      )}
    </Box>
  );
};

export default SearchFilterWizard;
